import { GOOGLE_MAPS_API_KEY, loadScript } from '../../../components/Location/helper';
import { getFormattedLocation } from '../../../services/formateLocation';
import { AddressType } from '../../../store/reducers/GetRates/Type';

export interface CurrentLocationResult {
  formattedLocation: AddressType & Record<string, any>;
  formattedAddress: string;
  place?: any;
}

/**
 * Ensures Google Maps JavaScript API is loaded in the browser document
 */
export const ensureGoogleMapsLoaded = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Window is not defined.'));
      return;
    }

    if ((window as any).google && (window as any).google.maps) {
      resolve();
      return;
    }

    if (!document.querySelector('#google-maps')) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
        document.querySelector('head'),
        'google-maps',
      );
    }

    // Poll until window.google.maps is available
    let elapsed = 0;
    const interval = 100;
    const timeout = 10000;

    const timer = setInterval(() => {
      elapsed += interval;
      if ((window as any).google && (window as any).google.maps) {
        clearInterval(timer);
        resolve();
      } else if (elapsed >= timeout) {
        clearInterval(timer);
        reject(new Error('Google Maps script load timed out.'));
      }
    }, interval);
  });
};

/**
 * Retrieves the current coordinates from the browser's Geolocation API
 */
export const getBrowserCoordinates = (): Promise<{ latitude: number; longitude: number }> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !('geolocation' in navigator)) {
      reject(new Error('Current location is not supported by this browser. Please enter the address manually.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      error => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            reject(
              new Error('Location permission was denied. Please allow location access or enter the address manually.'),
            );
            break;
          case error.POSITION_UNAVAILABLE:
            reject(
              new Error(
                'Location information is currently unavailable. Please try again or enter the address manually.',
              ),
            );
            break;
          case error.TIMEOUT:
            reject(new Error('Location request timed out. Please try again or enter the address manually.'));
            break;
          default:
            reject(new Error('Failed to obtain current location. Please enter the address manually.'));
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 30000,
      },
    );
  });
};

/**
 * Reverse geocodes the browser's current latitude and longitude using Google Maps Geocoder
 */
export const reverseGeocodeCurrentLocation = async (): Promise<CurrentLocationResult> => {
  // 1. Get browser GPS coordinates
  const { latitude, longitude } = await getBrowserCoordinates();

  // 2. Ensure Google Maps JS SDK is loaded
  await ensureGoogleMapsLoaded();

  // 3. Perform reverse geocoding via Google Maps Geocoder
  return new Promise((resolve, reject) => {
    const geocoder = new (window as any).google.maps.Geocoder();
    const latlng = { lat: latitude, lng: longitude };

    geocoder.geocode({ location: latlng }, (results: any[], status: string) => {
      if (status !== 'OK' || !results || results.length === 0) {
        reject(new Error('Unable to find address for current coordinates. Please enter the address manually.'));
        return;
      }

      const bestResult = results[0];
      const addressComponents = bestResult.address_components || [];

      // Extract line1, line2, and formatted location
      let streetNumber = '';
      let route = '';
      let sublocality = '';

      addressComponents.forEach((comp: any) => {
        const types = comp.types || [];
        if (types.includes('street_number')) streetNumber = comp.long_name;
        if (types.includes('route')) route = comp.long_name;
        if (types.includes('sublocality') || types.includes('sublocality_level_1')) sublocality = comp.long_name;
      });

      const parsedStandard = getFormattedLocation(addressComponents);
      const line1 = [streetNumber, route].filter(Boolean).join(' ') || sublocality || '';
      const line2 = line1 === sublocality ? '' : sublocality;

      const formattedLocation: AddressType & Record<string, any> = {
        city: parsedStandard.city || '',
        state: parsedStandard.state || '',
        country: parsedStandard.country || '',
        pincode: parsedStandard.pincode || '',
        line1,
        line2,
      };

      resolve({
        formattedLocation,
        formattedAddress: bestResult.formatted_address || '',
        place: bestResult,
      });
    });
  });
};
